import React, { useState, useRef, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import jwt_decode from 'jwt-decode';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import appapi from '../../../api/apiClient';
import { showmessage } from '../../../utils/toastr';
import { initUserData, login, refreshUserData } from '../../../redux/features/authSlice';
import { buttonStyle1 } from '../../../utils/commonStyle';
import { setLoading } from '../../../redux/features/appSlice';
import FormTextField from '../../../components/FormTextField';
import AuthLayout from '../../../components/AuthLayout';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const validationSchema = yup.object({
  oldPassword: yup
    .string()
    .required('Password is required'),
    // .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/, 'Enter a password of minimum 8 and maximum 20 characters with a combination of capital letters, small letters, symbols, and numbers.'),
  newPassword: yup
    .string()
    .required('Password is required')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/, 'Enter a password of minimum 8 and maximum 20 characters with a combination of capital letters, small letters, symbols, and numbers.'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required()
    .trim(),
});

export default function CreateNewPassword() {
  const theme = useTheme();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const { userId, user } = useSelector((state) => state.auth);

  const isMobile = useMediaQuery('(max-width:1000px)');

  const formRef = useRef();

  const navigate = useNavigate();
  const [visibility, setVisibility] = useState(false);
  const [visibility2, setVisibility2] = useState(false);
  const [visibility3, setVisibility3] = useState(false);

  const { state } = useLocation();

  const [data, setData] = useState({
    email: state?.email ? state?.email : '',
    password: '',
  });

  /**
   * @param {email, password} obj
   * Login user
   */

  const createPassword = async (obj) => {
    dispatch(setLoading(true));
    let payload = {
      userId,
      ...obj,
    };
    const response = await appapi.put(`user/createPassword`, payload);
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        dispatch(refreshUserData({ ...user, resetPassword: false }));
        showmessage(data.message, 'success');
      } else {
        showmessage(data.message, 'error');
      }
      dispatch(setLoading(false));
    } else {
      dispatch(setLoading(false));
      // showmessage(`Something Went Wrong!`, 2000, 'error')
    }
  };

  /**
   *
   * @returns redirect to forgotPassword Page
   */
  const onClickForgotPassword = (values, errors) => {
    if (!Boolean(errors.email)) {
      navigate(`/forgotPassword`, { state: { email: values.email } });
    } else {
      navigate(`/forgotPassword`, { state: { email: '' } });
    }
  };

  const resetForm = () => {
    return (
      <>
        <Stack style={{ backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
          <Typography
            className="fw-medium ff-Roboto createHeading !mt-[16px] !mx-[16px]"
            color={theme.palette.primary.text}
            fontSize={'16px'}
          >
            {t('Create New Password')}
          </Typography>
          <Stack style={{ backgroundColor: '#F2F2F2', width: '100%', height: '1px', marginTop: '10px' }} />
          <Typography
            className="fw-medium ff-Roboto  !mt-[16px] !mx-[16px] !mb-[5px]"
            color={theme.palette.primary.text}
            fontSize={'14px'}
          >
            {t('Please enter your new password and proceed')}
          </Typography>

          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              oldPassword: '',
              newPassword: '',
              confirmPassword: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => createPassword(values)}
          >
            {({ handleSubmit, values, handleChange, errors, touched, handleBlur, isValid, dirty }) => (
              <Form
                noValidate
                autoComplete="off"
              >
                <Stack
                  flex={1}
                  className="mt-9 mx-[16px]"
                >
                  <FormTextField
                    outlineLabel={t('Password')}
                    name="oldPassword"
                    type="password"
                    sx={{ mb: '14px' }}
                    visibility={visibility}
                    setVisibility={(val) => setVisibility(val)}
                  />
                </Stack>
                <Stack
                  flex={1}
                  className="mt-14 mx-[16px]"
                >
                  <FormTextField
                    outlineLabel={t('New Password')}
                    name="newPassword"
                    type="password"
                    sx={{ mb: '14px' }}
                    visibility={visibility2}
                    setVisibility={(val) => setVisibility2(val)}
                  />
                </Stack>
                <Stack
                  flex={1}
                  className="mt-14 mx-[16px]"
                >
                  <FormTextField
                    outlineLabel={t('Re-type password')}
                    name="confirmPassword"
                    type="password"
                    sx={{ mb: '14px' }}
                    visibility={visibility3}
                    setVisibility={(val) => setVisibility3(val)}
                  />
                </Stack>

                <Stack className="px-[16px] pb-[16px]">
                  <Button
                    sx={{
                      ...buttonStyle1,
                      width: '100%',
                      mt: '2.3px',
                      color: "#fff",
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                      },
                      '&:disabled': {
                        color: 'rgba(255,255,255,0.5)',
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                    disabled={!(dirty && isValid)}
                    type="submit"
                  >
                   {(t('Update'))}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </>
    );
  };

  return (
    <AuthLayout>
      <Stack
        style={{ maxWidth: '328px', minWidth: '328px'}}
        direction="row"
        alignSelf='center'
      >
        <Stack
          className="leftStack"
          style={{ width: '100%' }}
        >
          <Stack className="formSubmittedStyle mt-28">
            <Stack>
              <Typography
                color={'#FFFFFF'}
                className="fw-medium ff-Roboto"
                style={{ fontSize: '24px' }}
              >
                {t('CarbonWallet')}
              </Typography>
              <Typography
                color={'#FFFFFF'}
                className="fw-medium ff-Roboto"
                style={{ fontSize: '16px' }}
              >
               {t('By ZeroEmissions')}
              </Typography>
            </Stack>
            <div style={{ marginTop: '19px' }}>{resetForm()}</div>
          </Stack>
        </Stack>
      </Stack>
      <div style={{ position:'absolute', top: '16px', right: '16px'}}>
        <LanguageSwitcher />
      </div>
    </AuthLayout>
  );
}
